<template>
  <div class="rss-page">
    <div class="feed-head">
      <h2>{{ currentSource ? currentSource.name : 'RSS 订阅' }}</h2>
      <span class="sub" v-if="currentSource">共 {{ total }} 条 · {{ unreadCount }} 条未读</span>
      <div class="head-btns">
        <el-button size="small" icon="el-icon-plus" @click="openManage">管理订阅源</el-button>
        <el-button
          size="small"
          type="primary"
          icon="el-icon-refresh"
          :loading="refreshing"
          :disabled="!currentSource"
          @click="refresh"
        >刷新</el-button>
      </div>
    </div>

    <div v-if="!currentSource" class="empty-tip">
      请在左侧「订阅」文件夹中选择一个订阅源，或点「管理订阅源」新增。
    </div>

    <div v-else class="feed">
      <a
        v-for="post in posts"
        :key="post.id"
        :href="post.link"
        target="_blank"
        rel="noopener noreferrer"
        class="card"
        :class="{ read: isRead(post.guid) }"
        @click="markRead(post.guid)"
      >
        <div class="src">
          <span class="dot">{{ (currentSource.name || 'R').charAt(0).toUpperCase() }}</span>
          <span class="name">{{ currentSource.name }}</span>
          <span class="new" v-if="!isRead(post.guid)">NEW</span>
        </div>
        <div class="title">{{ post.title }}</div>
        <div class="desc" v-if="post.description">{{ post.description }}</div>
        <div class="meta">
          <span class="tag" v-if="post.category"># {{ post.category }}</span>
          <span class="author" v-if="post.author">{{ post.author }}</span>
          <span class="time">{{ post.pub_date }}</span>
        </div>
      </a>

      <div class="load-tip" v-if="loading">加载中…</div>
      <div class="load-tip" v-else-if="posts.length && !hasMore">没有更多了</div>
      <div class="empty-tip" v-else-if="!posts.length && !loading">
        暂无帖子，点右上角「刷新」抓取。
      </div>
    </div>

    <!-- 订阅源管理 -->
    <el-dialog title="订阅源管理" :visible.sync="manageVisible" width="720px">
      <el-form :model="form" label-width="80px" size="small">
        <el-row :gutter="12">
          <el-col :span="12">
            <el-form-item label="文件夹">
              <el-autocomplete
                v-model="form.folder_name"
                :fetch-suggestions="queryFolders"
                placeholder="选择或输入新文件夹"
                style="width:100%"
              />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="订阅名">
              <el-input v-model="form.name" placeholder="如：NodeSeek" />
            </el-form-item>
          </el-col>
          <el-col :span="24">
            <el-form-item label="RSS地址">
              <el-input v-model="form.url" placeholder="https://rss.nodeseek.com/" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="保留天数">
              <el-input-number v-model="form.retention_days" :min="1" :max="365" style="width:100%" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="排序">
              <el-input-number v-model="form.sort_order" :min="0" style="width:100%" />
            </el-form-item>
          </el-col>
        </el-row>
        <el-form-item>
          <el-button type="primary" size="small" @click="submitForm">{{ editingId ? '保存修改' : '新增订阅' }}</el-button>
          <el-button size="small" @click="resetForm">重置</el-button>
        </el-form-item>
      </el-form>

      <el-divider />

      <el-table :data="allSources" size="small" max-height="300">
        <el-table-column label="文件夹" prop="folder_name" width="110" />
        <el-table-column label="订阅名" prop="name" width="120" />
        <el-table-column label="地址" prop="url" show-overflow-tooltip />
        <el-table-column label="保留" prop="retention_days" width="60" />
        <el-table-column label="操作" width="120">
          <template #default="{ row }">
            <el-button type="text" size="mini" @click="editSource(row)">编辑</el-button>
            <el-button type="text" size="mini" style="color:#f56c6c" @click="deleteSource(row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-dialog>
  </div>
</template>

<script>
import axios from '@/config'

const READ_KEY = 'rss_read_guids'
const emptyForm = () => ({ folder_name: '', name: '', url: '', retention_days: 7, sort_order: 0 })

export default {
  name: 'RssFeed',
  data() {
    return {
      folders: [],        // [{ folder, sources: [...] }]
      posts: [],
      total: 0,
      page: 0,
      size: 20,
      hasMore: true,
      loading: false,
      refreshing: false,
      readMap: {},        // { guid: true }，已读集合（localStorage 持久化）
      manageVisible: false,
      editingId: null,
      form: emptyForm(),
      scrollParent: null,  // 真正的滚动容器（el-main），无限滚动监听挂在它上
    }
  },
  computed: {
    sourceId() {
      const id = this.$route.query.source
      return id ? Number(id) : null
    },
    allSources() {
      return this.folders.flatMap(f => f.sources)
    },
    currentSource() {
      return this.allSources.find(s => s.id === this.sourceId) || null
    },
    unreadCount() {
      return this.posts.filter(p => !this.isRead(p.guid)).length
    },
  },
  watch: {
    // 侧边栏切换订阅源时重新加载
    sourceId() {
      this.reload()
    },
  },
  async mounted() {
    this.loadReadMap()
    // el-main 是外层滚动容器，无限滚动监听挂到它上；找不到时退回自身
    this.scrollParent = this.$el.closest('.el-main') || this.$el
    this.scrollParent.addEventListener('scroll', this.onScroll)
    await this.fetchSources()
    this.reload()
  },
  beforeDestroy() {
    if (this.scrollParent) this.scrollParent.removeEventListener('scroll', this.onScroll)
  },
  methods: {
    // ---------- 已读状态（localStorage） ----------
    loadReadMap() {
      try {
        const arr = JSON.parse(localStorage.getItem(READ_KEY) || '[]')
        const map = {}
        arr.forEach(g => { map[g] = true })
        this.readMap = map
      } catch (e) { this.readMap = {} }
    },
    isRead(guid) {
      return !!this.readMap[guid]
    },
    markRead(guid) {
      if (this.readMap[guid]) return
      this.$set(this.readMap, guid, true)
      localStorage.setItem(READ_KEY, JSON.stringify(Object.keys(this.readMap)))
    },

    // ---------- 订阅源 ----------
    async fetchSources() {
      const res = await axios.get('rss/sources')
      if (res.data.code === '0000') this.folders = res.data.data
    },
    queryFolders(queryStr, cb) {
      const names = this.folders.map(f => ({ value: f.folder }))
      cb(queryStr ? names.filter(n => n.value.includes(queryStr)) : names)
    },

    // ---------- 帖子列表 + 无限滚动 ----------
    reload() {
      this.posts = []
      this.page = 0
      this.total = 0
      this.hasMore = true
      if (this.sourceId) this.loadMore()
    },
    async loadMore() {
      if (this.loading || !this.hasMore || !this.sourceId) return
      this.loading = true
      try {
        const res = await axios.get('rss/posts', {
          params: { source_id: this.sourceId, page: this.page + 1, size: this.size },
        })
        if (res.data.code === '0000') {
          const d = res.data.data
          this.posts.push(...d.items)
          this.total = d.total
          this.page = d.page
          this.hasMore = d.has_more
        } else {
          this.hasMore = false
        }
      } catch (e) {
        this.hasMore = false
      } finally {
        this.loading = false
        // 首屏未填满滚动容器时继续加载，保证无限滚动能被触发
        this.$nextTick(() => {
          const el = this.scrollParent
          if (this.hasMore && el && el.scrollHeight <= el.clientHeight + 10) {
            this.loadMore()
          }
        })
      }
    },
    onScroll() {
      const el = this.scrollParent
      if (!el) return
      if (el.scrollHeight - el.scrollTop - el.clientHeight < 200) {
        this.loadMore()
      }
    },
    async refresh() {
      if (!this.sourceId) return
      this.refreshing = true
      try {
        const res = await axios.post(`rss/refresh/${this.sourceId}`)
        if (res.data.code === '0000') {
          const r = (res.data.data && res.data.data[0]) || {}
          if (r.error) this.$message.warning('抓取失败：' + r.error)
          else this.$message.success(`新增 ${r.added || 0} 条，清理 ${r.removed || 0} 条`)
          this.reload()
          if (this.scrollParent) this.scrollParent.scrollTop = 0
        }
      } catch (e) {
        this.$message.error('刷新失败')
      } finally {
        this.refreshing = false
      }
    },

    // ---------- 管理对话框 ----------
    openManage() {
      this.resetForm()
      this.manageVisible = true
    },
    editSource(row) {
      this.editingId = row.id
      this.form = {
        folder_name: row.folder_name,
        name: row.name,
        url: row.url,
        retention_days: row.retention_days,
        sort_order: row.sort_order,
      }
    },
    resetForm() {
      this.editingId = null
      this.form = emptyForm()
    },
    async submitForm() {
      if (!this.form.name || !this.form.url) {
        this.$message.warning('订阅名和地址为必填')
        return
      }
      const payload = { ...this.form, folder_name: this.form.folder_name || '默认' }
      if (this.editingId) {
        await axios.put(`rss/sources/${this.editingId}`, payload)
        this.$message.success('修改成功')
      } else {
        await axios.post('rss/sources', payload)
        this.$message.success('新增成功')
      }
      this.resetForm()
      await this.fetchSources()
      this.$root.$emit('rss-sources-changed')  // 通知侧边栏刷新
    },
    async deleteSource(row) {
      await this.$confirm(`确认删除订阅源「${row.name}」？其下帖子会一并清除。`, '提示', { type: 'warning' })
      await axios.delete(`rss/sources/${row.id}`)
      this.$message.success('删除成功')
      await this.fetchSources()
      this.$root.$emit('rss-sources-changed')
      if (row.id === this.sourceId) this.reload()
    },
  },
}
</script>

<style scoped>
.rss-page {
  min-height: 100%;
  background: #f4f5f7;
  padding: 20px 0;
  margin: -20px;         /* 抵消 el-main 默认内边距，让灰底铺满 */
}
.feed-head {
  max-width: 760px;
  margin: 0 auto 16px;
  padding: 0 20px;
  display: flex;
  align-items: center;
  gap: 12px;
}
.feed-head h2 { font-size: 18px; font-weight: 600; margin: 0; }
.feed-head .sub { color: #909399; font-size: 13px; }
.head-btns { margin-left: auto; display: flex; gap: 8px; }
.feed { max-width: 760px; margin: 0 auto; padding: 0 20px; }
.card {
  display: block;
  background: #fff;
  border-radius: 10px;
  padding: 14px 16px;
  margin-bottom: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, .06);
  border: 1px solid transparent;
  text-decoration: none;
  color: inherit;
  transition: box-shadow .15s, transform .15s;
}
.card:hover { box-shadow: 0 4px 14px rgba(0, 0, 0, .1); transform: translateY(-1px); }
.card .src { display: flex; align-items: center; gap: 6px; margin-bottom: 6px; }
.card .src .dot {
  width: 18px; height: 18px; border-radius: 50%;
  background: #c8202f; color: #fff; font-size: 10px;
  line-height: 18px; text-align: center; font-weight: bold; flex-shrink: 0;
}
.card .src .name { color: #409eff; font-size: 13px; font-weight: 600; }
.card .src .new {
  background: #f56c6c; color: #fff; font-size: 10px;
  padding: 1px 6px; border-radius: 8px;
}
.card .title { color: #1a73c4; font-size: 15px; font-weight: 600; line-height: 1.5; margin-bottom: 5px; }
.card .desc {
  color: #606266; font-size: 13px; line-height: 1.6; margin-bottom: 10px;
  display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
}
.card .meta { display: flex; align-items: center; gap: 10px; font-size: 12px; color: #909399; }
.card .tag { color: #409eff; background: #ecf5ff; padding: 2px 8px; border-radius: 4px; }
.card .time { margin-left: auto; }
/* 已读置灰 */
.card.read { background: #f7f8fa; }
.card.read .title { color: #a0a4ab; font-weight: 500; }
.card.read .src .name { color: #b4b8bf; }
.card.read .desc { color: #b4b8bf; }
.card.read .src .dot { background: #c9ccd1; }
.card.read .tag { color: #b4b8bf; background: #f0f1f3; }
.load-tip, .empty-tip { text-align: center; color: #909399; font-size: 13px; padding: 16px 0; }
</style>
